import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { LoaderCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { apiClient } from '@/api/client'
import type { AuthResponse } from '@/features/auth/types'
import { useAuthStore } from '@/stores/auth-store'

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
})

function AuthCallbackPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent double-processing in StrictMode
    if (hasProcessed.current) return
    hasProcessed.current = true

    async function handleCallback() {
      const hash = window.location.hash
      const params = new URLSearchParams(hash.slice(1))
      const accessToken = params.get('access_token')

      if (!accessToken) {
        void navigate({ to: '/login' })
        return
      }

      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname)

      try {
        // Fetch user profile with the new token
        const { data } = await apiClient.get<AuthResponse['user']>('/auth/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        setAuth(data, accessToken)
        void navigate({ to: '/dashboard' })
      } catch {
        // If /me fails, still set the token and navigate
        // The user data will be fetched later
        void navigate({ to: '/login' })
      }
    }

    void handleCallback()
  }, [navigate, setAuth])

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <LoaderCircle className="size-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  )
}
